import React, {
  useState, useCallback, FC, useMemo, useRef, useEffect,
} from 'react';
import { Trans, useTranslation } from 'next-i18next';
import classNames from 'classnames';

import { useCurrencyContext } from 'src/common/providers/CurrencyProvider';
import RangeInput from 'src/common/components/RangeInput';
import getClassNames from 'src/common/utils/getClassNames';
import getPriceRange from 'src/search/utils/getPriceRange';
import { GA_EVENT_FILTER } from 'src/common/gaEvents';
import Typography from 'src/common/components/Typography';
import Icon from 'src/common/components/IconDS22';
import { trackEvent } from '../../../common/3rdParty/tracking';
import styles from './PriceFilter.module.scss';

export type PriceFilterProps = {
  values?: number[];
  searchHasResults: boolean;
  onChange: (minPrice: number, maxPrice: number) => void;
};

const HANDLE_SIZE = 26;
const GA_PARAM_PRICE_RANGE = 'p5';

const PriceFilter: FC<PriceFilterProps> = ({
  values,
  searchHasResults,
  onChange,
}) => {
  const [t] = useTranslation('search-page');
  const { currency } = useCurrencyContext();
  const {
    max: maxPrice,
    min: minPrice,
  } = getPriceRange(currency.code);
  const [initialMinPrice, initialMaxPrice] = values;
  const [rangeValues, setRangeValues] = useState<number[]>(
    (initialMinPrice && initialMaxPrice)
      ? [initialMinPrice, initialMaxPrice]
      : [minPrice, maxPrice],
  );
  const [isMaxPriceFocused, setMaxPriceFocus] = useState(false);
  const rangeTrackRef = useRef<HTMLDivElement>(null);
  const [currentMinPrice, currentMaxPrice] = rangeValues;

  const getTrackGap = useCallback((): number => {
    let gap = 1;
    if (rangeTrackRef.current) {
      const { offsetWidth } = rangeTrackRef.current;
      gap = (HANDLE_SIZE / offsetWidth) * maxPrice;
    }
    return Math.floor(gap);
  }, [maxPrice]);

  // eslint-disable-next-line max-len
  const isBallerMode = useMemo(() => (currentMinPrice >= maxPrice - getTrackGap()), [currentMinPrice, maxPrice, getTrackGap]);
  // eslint-disable-next-line max-len
  const isNoLoveMode = useMemo(() => (currentMaxPrice <= getTrackGap() && currentMinPrice === minPrice && !searchHasResults), [currentMaxPrice, currentMinPrice, minPrice, searchHasResults, getTrackGap]);

  useEffect(() => {
    setRangeValues(() => ([
      initialMinPrice || minPrice,
      initialMaxPrice || maxPrice,
    ]));
  }, [initialMinPrice, initialMaxPrice, minPrice, maxPrice]);

  const onFinalChange = useCallback(([newMinPrice, newMaxPrice]: number[]) => {
    if (isBallerMode || newMaxPrice === maxPrice) {
      onChange(newMinPrice, null);
    } else {
      onChange(newMinPrice, newMaxPrice);
    }
    trackEvent(GA_EVENT_FILTER, {
      [GA_PARAM_PRICE_RANGE]: [newMinPrice, newMaxPrice, currency.code],
    });
  }, [currency, isBallerMode, maxPrice, onChange]);

  const getPriceWithinLimits = useCallback((price: number): number => {
    if (price < minPrice) return minPrice;
    if (price > maxPrice) return maxPrice;
    return price;
  }, [minPrice, maxPrice]);

  const onMinPriceInputChange = useCallback((value: number) => {
    const price = getPriceWithinLimits(value);
    const newGap = currentMaxPrice - price;
    const trackGap = getTrackGap();
    const gapExceeded = newGap < trackGap;
    let finalRangeValues = [price, currentMaxPrice];

    if (gapExceeded) {
      const newMaxPrice = price + trackGap;
      if (newMaxPrice > maxPrice) {
        setRangeValues([currentMaxPrice - trackGap, currentMaxPrice]);
        finalRangeValues = [currentMaxPrice - trackGap, currentMaxPrice];
      } else {
        setRangeValues([price, newMaxPrice]);
        finalRangeValues = [price, newMaxPrice];
      }
    } else {
      setRangeValues([price, currentMaxPrice]);
    }
    onFinalChange(finalRangeValues);
  }, [getPriceWithinLimits, getTrackGap, onFinalChange, maxPrice, currentMaxPrice]);

  const onMaxPriceInputChange = useCallback((value: number) => {
    const price = getPriceWithinLimits(value);
    const newGap = price - currentMinPrice;
    const trackGap = getTrackGap();
    const gapExceeded = newGap < trackGap;
    let finalRangeValues = [currentMinPrice, price];

    if (gapExceeded) {
      const newMinPrice = price - trackGap;
      if (newMinPrice < minPrice) {
        setRangeValues([currentMinPrice, currentMinPrice + trackGap]);
        finalRangeValues = [currentMinPrice, currentMinPrice + trackGap];
      } else {
        setRangeValues([newMinPrice, price]);
        finalRangeValues = [newMinPrice, price];
      }
    } else {
      setRangeValues([currentMinPrice, price]);
    }
    onFinalChange(finalRangeValues);
  }, [getPriceWithinLimits, getTrackGap, onFinalChange, currentMinPrice, minPrice]);

  const onRangeInputChange = useCallback(([newMinPrice, newMaxPrice]: number[]) => {
    const newGap = newMaxPrice - newMinPrice;
    const trackGap = getTrackGap();
    if (newGap >= trackGap) {
      setRangeValues([newMinPrice, newMaxPrice]);
    }
    if (newMaxPrice === maxPrice && newGap < trackGap) {
      setRangeValues([maxPrice - trackGap, newMaxPrice]);
    }
    if (newMinPrice === minPrice && newGap < trackGap) {
      setRangeValues([newMinPrice, trackGap]);
    }
  }, [getTrackGap, maxPrice, minPrice]);

  const activeToolTip: string = useMemo(() => {
    if (isBallerMode) return t('price-filter-baller-mode');
    if (isNoLoveMode) return t('price-filter-no-love');
    return null;
  }, [isBallerMode, isNoLoveMode, t]);

  return (
    <div className={styles.root}>
      <div className={styles.rangeInputContainer}>
        <RangeInput
          min={minPrice}
          max={maxPrice}
          values={rangeValues}
          onChange={onRangeInputChange}
          onFinalChange={onFinalChange}
        />
        <div className={getClassNames(styles, 'toolTipContainer', classNames({ isBallerMode, isNoLoveMode }))}>
          {activeToolTip}
        </div>
      </div>
      <div className={styles.priceInputContainer} ref={rangeTrackRef}>
        <div className={styles.priceInput}>
          <label htmlFor="min-price">{t('price-filter-min-price')}</label>
          <span className={styles.inputWrapper}>
            {currency.symbol}
            <input
              id="min-price"
              type="number"
              min={minPrice}
              max={maxPrice}
              step={1}
              pattern="\d*"
              value={currentMinPrice || ''}
              onChange={(event) => {
                const price = Number(event.target.value);
                setRangeValues([price, currentMaxPrice]);
              }}
              onBlur={(event) => {
                const price = Number(event.target.value);
                onMinPriceInputChange(price);
              }}
              style={{ width: `${currentMinPrice.toString().length / 1.5}em` }}
            />
          </span>
        </div>
        <div className={styles.priceInput}>
          <label htmlFor="max-price">{t('price-filter-max-price')}</label>
          <span className={styles.inputWrapper}>
            {currency.symbol}
            <input
              id="max-price"
              type="number"
              min={minPrice}
              max={maxPrice}
              step={1}
              pattern="\d*"
              value={currentMaxPrice || ''}
              onChange={(event) => {
                const price = Number(event.target.value);
                setRangeValues([currentMinPrice, price]);
              }}
              onBlur={(event) => {
                const price = Number(event.target.value);
                onMaxPriceInputChange(price);
                setMaxPriceFocus(false);
              }}
              onFocus={() => setMaxPriceFocus(true)}
              style={{ width: `calc(${currentMaxPrice.toString().length / 1.5}em)` }}
            />
            {!isMaxPriceFocused && currentMaxPrice === maxPrice && (
              <span className={styles.priceInputSuffix}>
                +
              </span>
            )}
          </span>
        </div>
      </div>
      <div className={styles.copy}>
        <div className={styles.priceInfo}>
          <Icon
            id="question-filled"
            size="m"
            className={styles.icon}
          />
          <Typography type="span" size="body-m">
            <Trans
              t={t}
              i18nKey="filter-price--copy"
              components={{ bold: <strong /> }}
            />
          </Typography>
        </div>
      </div>
    </div>
  );
};

export default PriceFilter;
